/**
 * Policen — T1 List-View (UX-Inventar §5.2; backend Epic 9).
 * UI-U3 (AC 4): went LIVE via usePolicyRows (live GET /policies, mock fallback).
 * Defensive consumption of the backend-gaps-wave fields: Prämie renders `—` when
 * absent (never a fake 0); Betreuer/Branche/Risiko columns appear ONLY when a row
 * carries them; "Mehr laden" appears ONLY when the wire returns a next_cursor.
 *
 * DMS M12 additions (dms/spec.md §4.3, §4.6, §5.2, §M12):
 *   - Clickability rule: rows are NOT clickable as a whole and Kunde/Versicherer
 *     are plain text — ONLY the Policen-Nr. cell navigates to the policy detail
 *     (mirrors the reference screenshots exactly: Policen-Nr. is the sole blue
 *     link in the grid). The old whole-row `onRowClick` + Kunde/Versicherer
 *     EntityLinks are removed for this reason, not merely restyled.
 *   - "Verlauf" column: a non-navigating action button opening
 *     `PolicyHistoryDialog` ("See history" — reveals older/inactive versions,
 *     each openable in the M11 viewer or downloadable).
 *   - Volltext / Semantisch toggle: BOTH modes now call the SAME policy-scoped
 *     `/search?scope=policy` leg (§5.2, WEB-1 fix — wave dms-fixC). Volltext
 *     used to be a CLIENT-SIDE substring filter over the already-loaded grid
 *     rows (nr/kunde/versicherer/sparte) — it could never find a policy by
 *     its PDF CONTENT, because that text was never loaded into the grid in
 *     the first place. `searchPolicyScope` (the backend leg both modes hit)
 *     runs an identifier keyword leg + a content-semantic leg together —
 *     there's no separate keyword-only endpoint to map Volltext onto, so the
 *     toggle now only changes copy; the request is identical.
 *   - D2 fix: the toggle is a search-CRITERIA switch only; it must never
 *     swap the screen's structure. Both modes render the SAME standard
 *     `DataTable`; a non-empty query narrows + reorders `rows` to the ids the
 *     search leg returned (`displayRows`) instead of replacing the grid with
 *     a separate ranked hit-list layout. An empty query (either mode) always
 *     shows the plain, unfiltered grid, never a "type ≥2 characters" state.
 *   - ea/fix-search: the toggle moved from `ListView`'s `rightAction` slot
 *     (top-right, far from the search box) into the new `searchExtra` slot
 *     (same toolbar row, directly beside the search input) — same component,
 *     same wiring, purely a layout fix so it reads as "this toggle controls
 *     the search next to it."
 * [Source: docs/stories/ui-u3-core-restyle.md AC4/AC9]
 * [Source: projects/broker-ag/dms/spec.md §4.3, §4.6, §5.2, §M12]
 */
import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, History, Plus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { StatusDot } from '../../components/ui/StatusDot';
import { TableCell } from '../../components/ui/Table';
import { EntityLink } from '../../components/ui/EntityLink';
import { Toast } from '../../components/ui/Toast';
import { cn } from '../../lib/cn';
import { chf } from '../../lib/format';
import { apiClient } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { POLICY_STATUS_META } from '../entity-detail/mock';
import { usePolicyRows } from './hooks/usePolicyRows';
import { usePolicySemanticSearch } from './hooks/usePolicySemanticSearch';
import { SemanticDegradedNotice } from '../search/SemanticDegradedNotice';
import { PolicyHistoryDialog } from './components/PolicyHistoryDialog';
import { UploadPolicyDialog, type HolderOption, type PolicyDocKind } from './components/UploadPolicyDialog';
import type { PolicyListRow } from './api/policies-api';

const num = 'text-right mono-num';

/**
 * Volltext vs Semantisch (dms/spec.md §5.2, WEB-1 fix — wave dms-fixC): both now hit
 * the SAME policy-scoped `/search?scope=policy` leg when there's a query — see the
 * `searchActive` derivation below for exactly when it fires. D2 fix: the result only
 * ever narrows/reorders the standard grid; it never swaps to a different layout.
 */
type SearchMode = 'volltext' | 'semantisch';

export function PoliciesScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const live = isLiveData();
  const perms = useEffectivePermissions();
  const { rows, nextCursor, isLoading, isError } = usePolicyRows();

  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<SearchMode>('volltext');
  const [historyFor, setHistoryFor] = useState<PolicyListRow | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  function showFeedback(msg: string) {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 3000);
  }

  /** Live holder typeahead for the "Neue Police" dialog (Policies-page entry point). */
  async function handleSearchHolders(q: string): Promise<HolderOption[]> {
    if (!live) return [];
    const res = await apiClient.customers.list({ q, limit: 8 });
    return res.items
      .filter((c) => c.name)
      .map((c) => ({
        id: c.subject_id,
        name: c.name as string,
        subtitle: [c.subject_type === 'firma' ? 'Firma' : 'Person', c.city].filter(Boolean).join(' · '),
      }));
  }

  async function handleUploadPolicy(
    file: File,
    opts: { holderId: string; docKind: PolicyDocKind },
  ) {
    if (!live) {
      showFeedback('Police wird hochgeladen und analysiert …');
      return { document_id: 'mock-doc', job_id: 'mock-job', status: 'queued' };
    }
    const res = await apiClient.policies.upload(file, opts);
    // The Police is created asynchronously (extraction queue) — no policy id to navigate to
    // yet; refresh the grid so it appears once the worker finishes, and confirm via toast.
    await qc.invalidateQueries({ queryKey: ['policies'] });
    showFeedback('Police wird hochgeladen und analysiert, sie erscheint gleich in der Liste.');
    return res;
  }

  const needle = search.trim();

  // WEB-1 fix: Volltext used to filter the already-loaded rows client-side and could
  // never find a policy by PDF CONTENT. Now it fires the SAME server search as
  // Semantisch whenever there's a query, regardless of mode.
  const policySearch = usePolicySemanticSearch(search, needle.length > 0);
  const policyHits = policySearch.hits.filter((h) => h.type === 'police');
  // D2 fix: `searched` (query ≥ min length) is the ONLY thing that gates filtering;
  // an empty query always shows the plain grid, in either mode.
  const searchActive = policySearch.searched;
  // S4 follow-up: only in Semantisch mode. Both modes hit the same hybrid leg (WEB-1),
  // but a broker who asked for Volltext got what they asked for - telling them the
  // semantic half is down would be noise about a leg they did not request.
  const semanticDegraded =
    searchActive && mode === 'semantisch' && !policySearch.semanticAvailable;

  const hitRank = useMemo(() => {
    const map = new Map<string, number>();
    policyHits.forEach((hit, i) => map.set(hit.id, i));
    return map;
  }, [policyHits]);

  // D2 fix: same rows, same `DataTable`; a search only narrows + reorders which
  // ones are shown, it never swaps in a different layout.
  const displayRows = useMemo(() => {
    if (!searchActive) return rows;
    return rows
      .filter((r) => hitRank.has(r.id))
      .sort((a, b) => hitRank.get(a.id)! - hitRank.get(b.id)!);
  }, [rows, searchActive, hitRank]);

  const hasBranche = rows.some((r) => r.branche !== undefined);
  const hasBetreuer = rows.some((r) => r.betreuer !== undefined);
  const hasRisiko = rows.some((r) => r.risiko !== undefined);

  const totalPraemie = displayRows.reduce((acc, p) => acc + (p.praemie ?? 0), 0);
  const anyPraemie = displayRows.some((p) => p.praemie !== null);

  const columns = useMemo<ColumnDef<PolicyListRow>[]>(() => {
    const cols: ColumnDef<PolicyListRow>[] = [
      {
        accessorKey: 'nr',
        header: 'Policen-Nr.',
        cell: ({ row }) => (
          <EntityLink
            onActivate={() => void navigate({ to: '/policen/$id', params: { id: row.original.id } })}
            className={cn('font-medium mono-num')}
          >
            {row.original.nr}
          </EntityLink>
        ),
      },
      {
        accessorKey: 'kunde',
        header: 'Kunde',
        // Clickability rule (dms/spec.md §4.6): ONLY Policen-Nr. navigates — Kunde
        // is plain text here (was an EntityLink; removed on purpose, not an oversight).
        cell: ({ row }) => <span className="text-foreground">{row.original.kunde}</span>,
      },
      {
        accessorKey: 'versicherer',
        header: 'Versicherer',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.versicherer}</span>
        ),
      },
    ];

    if (hasBranche) {
      cols.push({
        accessorKey: 'branche',
        header: 'Branche',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.branche ?? '-'}</span>
        ),
      });
    }

    cols.push({
      accessorKey: 'sparte',
      header: 'Sparte',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.sparte}</span>,
    });

    if (hasBetreuer) {
      cols.push({
        accessorKey: 'betreuer',
        header: 'Betreuer',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.betreuer ?? '-'}</span>
        ),
      });
    }

    cols.push({
      accessorKey: 'praemie',
      header: 'Prämie',
      cell: ({ row }) => (
        <div className={num}>{row.original.praemie === null ? '-' : chf(row.original.praemie)}</div>
      ),
    });

    cols.push({
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const m = POLICY_STATUS_META[row.original.status];
        return <StatusDot tone={m.tone} label={m.label} />;
      },
    });

    if (hasRisiko) {
      cols.push({
        accessorKey: 'risiko',
        header: 'Risiko',
        cell: ({ row }) =>
          row.original.risiko ? (
            <Badge variant="danger">
              <AlertTriangle aria-hidden /> Risiko
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      });
    }

    cols.push({
      accessorKey: 'renewalTage',
      header: 'Nächstes Renewal',
      cell: ({ row }) => {
        const t = row.original.renewalTage;
        const overdue = t < 0;
        const soon = t >= 0 && t <= 14;
        const label =
          t < 0
            ? `überfällig seit ${Math.abs(t)} ${Math.abs(t) === 1 ? 'Tag' : 'Tagen'}`
            : t === 0
              ? 'heute fällig'
              : `in ${t} ${t === 1 ? 'Tag' : 'Tagen'}`;
        return (
          <StatusDot
            tone={overdue ? 'danger' : soon ? 'warning' : 'neutral'}
            label={label}
          />
        );
      },
    });

    // "See history" (dms/spec.md §4.3, §M12) — a non-navigating action button
    // (opens PolicyHistoryDialog), NOT another link — the clickability rule
    // (§4.6) only reserves NAVIGATION for the Policen-Nr. cell.
    cols.push({
      id: 'verlauf',
      header: 'Verlauf',
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => setHistoryFor(row.original)}>
          <History className="size-3.5" /> Verlauf anzeigen
        </Button>
      ),
    });

    return cols;
  }, [hasBranche, hasBetreuer, hasRisiko, navigate]);

  // Summary row: one cell per column, Prämie total aligned under its column.
  const summary = (
    <>
      {columns.map((col, i) => {
        const key = (col as { accessorKey?: string; id?: string }).accessorKey ?? (col as { id?: string }).id ?? String(i);
        if (i === 0) return <TableCell key={key} className="font-medium">Summe</TableCell>;
        if (key === 'praemie') {
          return (
            <TableCell key={key} className={`font-medium ${num}`}>
              {anyPraemie ? chf(totalPraemie) : '-'}
            </TableCell>
          );
        }
        return <TableCell key={key} />;
      })}
    </>
  );

  const renewalCount = rows.filter((p) => p.status === 'renewal').length;
  const baldFaelligCount = rows.filter((p) => p.renewalTage <= 30).length;

  return (
    <>
      <ListView
        title="Policen"
        views={[
          { label: 'Alle', count: rows.length },
          { label: 'Aktiv', count: rows.filter((p) => p.status === 'aktiv').length },
          { label: 'In Renewal', count: renewalCount },
          { label: 'Bald fällig', count: baldFaelligCount },
        ]}
        count={searchActive ? `${displayRows.length} Treffer` : `${rows.length} von ${rows.length}`}
        searchPlaceholder={
          mode === 'semantisch'
            ? 'Semantisch suchen …'
            : 'Volltext: Policen-Nr., Kunde, Carrier, Sparte, Dokumentinhalt …'
        }
        searchValue={search}
        onSearchChange={setSearch}
        searchNotice={semanticDegraded ? <SemanticDegradedNotice /> : undefined}
        searchExtra={
          // ea/fix-search: moved out of `rightAction` (top-right, far from the
          // search box) to sit directly beside the search input — it's the
          // toggle for THIS search, not a page-level action.
          <SegmentedControl
            ariaLabel="Suchmodus"
            value={mode}
            onValueChange={(v) => setMode(v as SearchMode)}
            options={[
              { value: 'volltext', label: 'Volltext' },
              { value: 'semantisch', label: 'Semantisch' },
            ]}
          />
        }
        rightAction={
          perms.can('policy.write') ? (
            <Button size="sm" onClick={() => setUploadOpen(true)}>
              <Plus className="size-3.5" /> Neue Police
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
            Lade Policen …
          </div>
        ) : isError ? (
          <div
            role="alert"
            className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground"
          >
            Policen konnten nicht geladen werden.
          </div>
        ) : searchActive && policySearch.isLoading ? (
          // D2 fix: while the debounced search resolves, keep the SAME table
          // shell (no layout swap), just a loading message in its place.
          <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
            Suche läuft …
          </div>
        ) : searchActive && displayRows.length === 0 ? (
          <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
            Keine Treffer für «{needle}».
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
            Keine Policen vorhanden.
          </div>
        ) : (
          <>
            <DataTable columns={columns} data={displayRows} summary={summary} />
            {!searchActive && nextCursor && (
              <div className="mt-3 flex justify-center">
                <Button variant="outline" size="sm">
                  Mehr laden
                </Button>
              </div>
            )}
          </>
        )}
      </ListView>

      {historyFor && (
        <PolicyHistoryDialog
          policyId={historyFor.id}
          policyNr={historyFor.nr}
          open={historyFor !== null}
          onClose={() => setHistoryFor(null)}
        />
      )}

      <UploadPolicyDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUploadPolicy}
        onSearchHolders={handleSearchHolders}
        onUploaded={() => {
          /* handleUploadPolicy already refreshes + toasts on success */
        }}
      />

      {feedback && <Toast tone="ok">{feedback}</Toast>}
    </>
  );
}
